import type { ReactNode, SyntheticEvent } from "react"
import Link from "next/link"

import { Button, type ButtonProps } from "@coval/ui/button"
import { cn } from "@coval/ui/cn"
import { Tooltip } from "@coval/ui/tooltip"
import { Plus } from "lucide-react"

type ResourceActionButtonProps = Omit<
  ButtonProps,
  | "asChild"
  | "children"
  | "disabledReason"
  | "icon"
  | "tooltip"
  | "tooltipProps"
> & {
  href?: string
  resourceName: string
}

type ValidationIssue = string | null | undefined | false

const stopPropagation = (event: SyntheticEvent) => {
  event.stopPropagation()
}

const DISABLED_REASON_WRAPPER_HANDLERS = {
  onClick: stopPropagation,
  onMouseDown: stopPropagation,
  onMouseUp: stopPropagation,
  onPointerDown: stopPropagation,
  onPointerUp: stopPropagation,
} as const

interface CreateResourceButtonProps extends Omit<
  ResourceActionButtonProps,
  "href"
> {
  disabledReason?: ReactNode
  onValidationAttempt?: () => void
  validationIssues?: readonly ValidationIssue[]
}

interface SaveResourceButtonProps extends Omit<
  ResourceActionButtonProps,
  "href"
> {
  disabledReason?: ReactNode
  onValidationAttempt?: () => void
  validationIssues?: readonly ValidationIssue[]
}

export function getNewResourceLabel(resourceName: string): string {
  return `New ${resourceName}`
}

function getCreateResourceLabel(resourceName: string): string {
  return `Create ${resourceName}`
}

function getCreatingResourceLabel(resourceName: string): string {
  return `Creating ${resourceName}…`
}

function getSaveResourceLabel(): string {
  return "Save"
}

function getSavingResourceLabel(): string {
  return "Saving…"
}

function ResourceActionButton({
  action,
  compact = false,
  disabledReason,
  href,
  onValidationAttempt,
  resourceName,
  type = "button",
  loading = false,
  validationIssues = [],
  ...buttonProps
}: ResourceActionButtonProps & {
  action: "new" | "create" | "save"
  compact?: boolean
  disabledReason?: ReactNode
  onValidationAttempt?: () => void
  validationIssues?: readonly ValidationIssue[]
}) {
  const label =
    action === "new"
      ? getNewResourceLabel(resourceName)
      : action === "create"
        ? getCreateResourceLabel(resourceName)
        : getSaveResourceLabel()
  const loadingLabel =
    action === "create"
      ? getCreatingResourceLabel(resourceName)
      : getSavingResourceLabel()
  const content =
    loading && action !== "new" ? (
      loadingLabel
    ) : action === "save" ? (
      label
    ) : (
      <>
        <Plus aria-hidden />
        {!compact && label}
      </>
    )

  const normalizedValidationIssues = Array.from(
    new Set(
      validationIssues.flatMap((issue) => {
        if (typeof issue !== "string") {
          return []
        }
        const trimmedIssue = issue.trim()
        if (!trimmedIssue) {
          return []
        }
        const normalizedIssue = /[.!?]$/.test(trimmedIssue)
          ? trimmedIssue
          : `${trimmedIssue}.`
        return [normalizedIssue]
      }),
    ),
  )
  const hasValidationIssues =
    action !== "new" && normalizedValidationIssues.length > 0
  const showsValidationIssues =
    hasValidationIssues && !loading && !buttonProps.disabled
  const showsDisabledReason =
    disabledReason != null && !loading && Boolean(buttonProps.disabled)
  const isDisabled = buttonProps.disabled || loading || hasValidationIssues
  const controlClassName = cn(
    buttonProps.className,
    (showsValidationIssues || showsDisabledReason) && "pointer-events-none",
  )

  // Header actions sit below the sticky navbar, so compact labels open beneath it.
  const compactProps = compact
    ? {
        icon: true as const,
        tooltip: label,
        tooltipProps: { side: "bottom" as const },
      }
    : {}

  if (href) {
    if (isDisabled) {
      return (
        <Button
          {...buttonProps}
          {...compactProps}
          type={type}
          loading={loading}
          disabled={isDisabled}
          className={controlClassName}
          aria-label={buttonProps["aria-label"] ?? label}
        >
          {content}
        </Button>
      )
    }
    return (
      <Button
        asChild
        loading={loading}
        {...buttonProps}
        {...compactProps}
        className={controlClassName}
      >
        <Link href={href}>{content}</Link>
      </Button>
    )
  }

  const button = (
    <Button
      {...buttonProps}
      {...compactProps}
      type={type}
      loading={loading}
      disabled={isDisabled}
      className={controlClassName}
      tabIndex={
        showsValidationIssues || showsDisabledReason ? -1 : buttonProps.tabIndex
      }
    >
      {content}
    </Button>
  )

  if (showsDisabledReason) {
    return (
      <Tooltip content={disabledReason} side="bottom">
        <span
          className="inline-flex rounded-md focus-visible:outline-none"
          tabIndex={0}
          role="button"
          aria-disabled
          aria-label={label}
          {...DISABLED_REASON_WRAPPER_HANDLERS}
        >
          <span aria-hidden className="inline-flex">
            {button}
          </span>
        </span>
      </Tooltip>
    )
  }

  if (!showsValidationIssues) {
    return button
  }

  // Resource actions frequently sit below the sticky navbar, where a top
  // tooltip would be covered.
  return (
    <Tooltip
      className="max-w-none"
      content={
        <div className="max-w-72 space-y-1 text-left">
          <p className="font-medium">
            To {action} {resourceName}:
          </p>
          <ul className="list-disc space-y-0.5 pl-4">
            {normalizedValidationIssues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </div>
      }
      side="bottom"
    >
      <span
        className="inline-flex rounded-md focus-visible:outline-none"
        tabIndex={0}
        role="button"
        aria-disabled
        aria-label={label}
        onClick={onValidationAttempt}
        onKeyDown={(event) => {
          if (
            onValidationAttempt &&
            (event.key === "Enter" || event.key === " ")
          ) {
            event.preventDefault()
            onValidationAttempt()
          }
        }}
      >
        <span aria-hidden className="inline-flex">
          {button}
        </span>
      </span>
    </Tooltip>
  )
}

export function NewResourceButton({
  ...props
}: ResourceActionButtonProps & { compact?: boolean }) {
  return <ResourceActionButton action="new" {...props} />
}

export function CreateResourceButton({ ...props }: CreateResourceButtonProps) {
  return <ResourceActionButton action="create" {...props} />
}

export function SaveResourceButton({ ...props }: SaveResourceButtonProps) {
  return <ResourceActionButton action="save" {...props} />
}
